import { forwardRef } from 'react'

import { styled } from 'styled-components'

import Icon, { IconProps } from 'core/components/Icon/Icon'
import Spinner from 'core/components/lib/Spinner'
import { PendingEventHandler, usePendingCallback } from 'core/hooks'
import { onSurfaceDim, onSurfaceVariant, outlineMuted, secondary } from 'core/styles'

type IconButtonProps = IconProps & {
  onClick?: PendingEventHandler
  checked?: boolean
  disabled?: boolean
  isLoading?: boolean
  className?: string
  ariaLabel?: string
  type?: 'submit' | 'reset' | 'button' | undefined
}

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      onClick: _onClick,
      checked,
      disabled = false,
      isLoading = false,
      fontSize = '24px',
      color = onSurfaceVariant,
      className,
      ariaLabel,
      type = 'button',
      name,
      outlined,
      ...rest
    },
    ref,
  ) => {
    const [isPending, onClick] = usePendingCallback(_onClick)

    return isPending || isLoading ?
        <Spinner height={fontSize} width={fontSize} />
      : <StyledButton
          aria-checked={checked === undefined ? undefined : checked}
          aria-label={ariaLabel}
          className={className}
          color={
            disabled ? outlineMuted
            : checked === false ?
              onSurfaceDim
            : color
          }
          disabled={disabled}
          onClick={onClick}
          ref={ref}
          type={type}
          {...rest}
        >
          <Icon fontSize={fontSize} name={name} outlined={outlined} />
        </StyledButton>
  },
)

IconButton.displayName = 'IconButton'

export default IconButton

const StyledButton = styled.button<{ color?: string; margin?: string }>`
  transform-origin: center;
  margin: ${(p) => p.margin ?? '0'};
  border: none;
  background: none;
  cursor: pointer;
  color: ${(p) => p.color};

  &:hover,
  &:focus-visible {
    transform: scale(1.1);
  }

  &:disabled {
    transform: scale(1);
    cursor: initial;
  }

  &:focus-visible {
    outline: 1px solid ${secondary};
  }

  &:active {
    transform: none;
  }
`
